import { Link, NavLink } from "react-router-dom";
import { ShoppingBag, Menu, X } from "lucide-react";
import { useState, useEffect } from "react";
import { useCart } from "@/context/CartContext";
import { useLang } from "@/context/LangContext";

const Header = () => {
  const { count, setOpen } = useCart();
  const { lang, setLang, t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const linkCls = ({ isActive }) =>
    `text-sm tracking-wide transition-opacity hover:opacity-60 ${
      isActive ? "opacity-100" : "opacity-80"
    }`;

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-40 border-b transition-colors ${
        scrolled
          ? "bg-[#F5F0EB]/95 backdrop-blur border-[#dcd7ce]"
          : "bg-[#F5F0EB] border-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 sm:px-10 md:px-16 h-16 md:h-20 flex items-center justify-between">
        <Link
          to="/"
          data-testid="header-logo"
          className="font-serif-display text-xl md:text-2xl tracking-tight"
        >
          myFLOVEA
        </Link>

        <nav className="hidden md:flex items-center gap-10">
          <NavLink
            to="/"
            data-testid="nav-shop"
            className={linkCls}
            end
          >
            {t.nav.shop}
          </NavLink>
          <NavLink to="/story" data-testid="nav-story" className={linkCls}>
            {t.nav.story}
          </NavLink>
          <NavLink to="/blog" data-testid="nav-blog" className={linkCls}>
            {lang === "fr" ? "Journal" : "Journal"}
          </NavLink>
          <NavLink to="/faq" data-testid="nav-faq" className={linkCls}>
            {t.nav.faq}
          </NavLink>
          <NavLink to="/contact" data-testid="nav-contact" className={linkCls}>
            {t.nav.contact}
          </NavLink>
        </nav>

        <div className="flex items-center gap-4 md:gap-6">
          <div
            className="hidden md:flex items-center text-[11px] tracking-[0.18em] uppercase"
            data-testid="lang-switcher"
          >
            <button
              type="button"
              onClick={() => setLang("fr")}
              data-testid="lang-fr"
              className={`px-1 ${lang === "fr" ? "opacity-100" : "opacity-40 hover:opacity-70"}`}
            >
              FR
            </button>
            <span className="opacity-30">/</span>
            <button
              type="button"
              onClick={() => setLang("en")}
              data-testid="lang-en"
              className={`px-1 ${lang === "en" ? "opacity-100" : "opacity-40 hover:opacity-70"}`}
            >
              EN
            </button>
          </div>

          <button
            type="button"
            data-testid="open-cart-button"
            onClick={() => setOpen(true)}
            className="relative flex items-center justify-center -mr-2 p-3 group"
            aria-label="Open cart"
          >
            <ShoppingBag
              size={22}
              strokeWidth={1.4}
              className="transition-transform group-hover:-rotate-6"
            />
            {count > 0 && (
              <span
                data-testid="cart-count"
                className="absolute top-1.5 right-1 bg-[#1A1A1A] text-[#F5F0EB] text-[10px] rounded-full h-4 min-w-4 px-1 inline-flex items-center justify-center"
              >
                {count}
              </span>
            )}
          </button>

          <button
            type="button"
            data-testid="open-mobile-menu"
            className="md:hidden -mr-2 p-3 flex items-center justify-center"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="Open menu"
            aria-expanded={mobileOpen}
          >
            {mobileOpen ? <X size={22} strokeWidth={1.4} /> : <Menu size={22} strokeWidth={1.4} />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div
          data-testid="mobile-menu"
          className="md:hidden border-t border-[#dcd7ce] bg-[#F5F0EB]"
        >
          <div className="px-6 py-4 flex flex-col">
            {[
              { to: "/", label: t.nav.shop, id: "m-shop" },
              { to: "/story", label: t.nav.story, id: "m-story" },
              { to: "/blog", label: lang === "fr" ? "Journal" : "Journal", id: "m-blog" },
              { to: "/faq", label: t.nav.faq, id: "m-faq" },
              { to: "/contact", label: t.nav.contact, id: "m-contact" },
            ].map((l) => (
              <Link
                key={l.id}
                to={l.to}
                data-testid={`mobile-${l.id}`}
                onClick={() => setMobileOpen(false)}
                className="text-base py-3 border-b border-[#dcd7ce]/50 last:border-b-0"
              >
                {l.label}
              </Link>
            ))}
            <div className="pt-4 mt-3 border-t border-[#dcd7ce] flex items-center gap-2 text-[11px] tracking-[0.18em] uppercase">
              <button
                type="button"
                onClick={() => setLang("fr")}
                data-testid="mobile-lang-fr"
                className={`px-4 py-2 rounded-sm ${lang === "fr" ? "opacity-100 bg-[#ebe7df]" : "opacity-50"}`}
              >
                FR
              </button>
              <button
                type="button"
                onClick={() => setLang("en")}
                data-testid="mobile-lang-en"
                className={`px-4 py-2 rounded-sm ${lang === "en" ? "opacity-100 bg-[#ebe7df]" : "opacity-50"}`}
              >
                EN
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
